import React from "react";
import { Crown, Star } from "lucide-react";

// UI reserved for the OG roles (see `isLifetimeOgUser` / `isOgUser`). Each tier
// wears its own pill next to the name in the header, and the card that floats
// under it on the Loads page carries the detail — lifetime OG holds unlimited
// tokens, plain OG holds a one-year grant of free ones. The two pills echo
// their cards: purple and a crown for lifetime, dark and a gold star for OG.

/** Free tokens granted with an OG membership. */
// TODO: no API field carries this yet — swap the constant for the real value
// once the backend exposes it.
const OG_FREE_TOKENS = 100;

/** The purple "Lifetime OG" pill shown next to the username in the header. */
export const LifetimeOgBadge: React.FC = () => (
  <span className="flex items-center gap-1 sm:gap-1.5 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#4C1D95] pl-1.5 pr-2.5 sm:pl-2 sm:pr-3 py-1 sm:py-1.5 whitespace-nowrap">
    <Crown className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[#FBBF24]" fill="#FBBF24" />
    <span className="text-[11px] sm:text-[12px] font-semibold leading-none text-white">
      Lifetime OG
    </span>
  </span>
);

/** The "OG User" pill — plain OG's counterpart to the Lifetime OG one. */
export const OgUserBadge: React.FC = () => (
  <span className="flex items-center gap-1 sm:gap-1.5 rounded-full border border-[#FBBF24] bg-gradient-to-r from-[#1F2937] to-[#0B1220] pl-1.5 pr-2.5 sm:pl-2 sm:pr-3 py-1 sm:py-1.5 whitespace-nowrap">
    <Star className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[#FBBF24]" fill="#FBBF24" />
    <span className="text-[11px] sm:text-[12px] font-semibold leading-none text-white">
      OG User
    </span>
  </span>
);

const LogoSIcon = "/images/logo_s.svg";
const InfinityIcon = "/images/infinity.svg";

/** The "Unlimited Tokens" card — the token balance card's lifetime-OG twin.
 * Kept as the single source of truth for this card (Sidebar + Credits page
 * both render it) so the two never drift apart again. */
export const LifetimeOgTokenCard: React.FC = () => (
  <div className="relative overflow-hidden bg-[#0A0A0A] rounded-2xl p-3">
    <div
      className="pointer-events-none absolute -bottom-[30px] -right-[30px] h-[110px] w-[110px] rounded-full blur-[22px]"
      style={{
        background:
          "radial-gradient(circle, rgba(251,191,36,0.35) 0%, rgba(251,191,36,0) 70%)",
      }}
    />
    <div className="relative">
      <span
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-black text-xs font-manrope font-semibold leading-none whitespace-nowrap"
        style={{
          backgroundImage: "linear-gradient(173.67deg, #FEC400 10.49%, #FF8C00 124.4%)",
        }}
      >
        <Crown className="w-3 h-3" fill="currentColor" />
        Lifetime OG
      </span>
      <p className="text-white text-base font-manrope font-bold mt-2 whitespace-nowrap">
        Unlimited Tokens
      </p>
      <div className="flex items-center gap-2.5 mt-2.5">
        <img src={LogoSIcon} alt="" className="h-11 w-11 object-contain shrink-0" />
        <div className="flex flex-col items-start gap-0.5 min-w-0">
          <img src={InfinityIcon} alt="Unlimited" className="h-8 w-[46px] object-contain" />
          <p className="text-white/[0.82] text-xs leading-tight font-manrope font-medium whitespace-nowrap">
            Balance Tokens
          </p>
        </div>
      </div>
    </div>
  </div>
);

/** The OG card: membership, validity and the free-token grant. */
export const OgUserTokenCard: React.FC = () => (
  <div className="w-[100px] rounded-xl border border-[#FBBF24] bg-gradient-to-b from-[#1F2937] to-[#0B1220] px-3 py-2.5 text-center shadow-lg">
    <p className="flex items-center justify-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#FBBF24]">
      <Star className="w-3 h-3" fill="currentColor" />
      OG User
    </p>
    
    <p className="mt-1.5 text-[26px] font-bold leading-none text-white">
      {OG_FREE_TOKENS}
    </p>
    <p className="mt-1 text-center text-[10px] leading-none text-white/70">
      Free Tokens
    </p>
  </div>
);
